/**
 * Contact Edit Modal
 * Pipedrive-style modal for editing/creating leads in the CRM
 * Includes activity log for tracking interactions
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { useSession } from '@/features/auth/hooks/useSession';
import { useIsCompactLayout } from '@/common/hooks';
import type { CRMColumnConfig } from '@/types/modules';
import { LABEL_COLORS, DEFAULT_LABEL_COLOR, getAvatarColorPair, getInitials as getSharedInitials } from '../shared/colors';
import { DraftVariantSelector, type DraftVariant } from './DraftVariantSelector';
import { ComposeEmailModal } from './ComposeEmailModal';
import { getAuthItem } from '@/lib/authStorage';
import { isAutoEnrichOptedOut, isAlreadyEnriched, type EnrichmentContact } from './contactEnrichmentUtils';
import type { ContactListDef } from './contactLists';
import { emailApiBase, emailApiPath } from '@/lib/email-api';
import { http } from '@/lib/http';
import { formatNumberWithCommas } from './contactEditModal/utils';
import { DeleteContactConfirmationModal } from './contactEditModal/components/DeleteContactConfirmationModal';
import { UnsavedChangesConfirmationModal } from './contactEditModal/components/UnsavedChangesConfirmationModal';
import { CompleteFollowUpModal } from './contactEditModal/components/CompleteFollowUpModal';
import { ContactEditHeaderBar } from './contactEditModal/components/ContactEditHeaderBar';
import { ContactEditFreeformPanel } from './contactEditModal/components/ContactEditFreeformPanel';
import { ContactEditFormBody } from './contactEditModal/components/ContactEditFormBody';
import { ContactEditDesktopTimeline } from './contactEditModal/components/ContactEditDesktopTimeline';
import { ContactEditDesktopComposer } from './contactEditModal/components/ContactEditDesktopComposer';
import { ContactEditMobileActivitySheet } from './contactEditModal/components/ContactEditMobileActivitySheet';
import type { Activity, ContactObject } from './contactEditModal/lib/types';
import { activitiesToNotes } from './contactEditModal/lib/contactMapping';
import { groupActivitiesByMonth, determineEmailDirection } from './contactEditModal/lib/contactTimeline';
import { type EmailAttachment, handleEmailFileSelect as handleEmailFileSelectImpl, removeEmailAttachment as removeEmailAttachmentImpl } from './contactEditModal/lib/emailAttachments';
import { searchGmailPeople as searchGmailPeopleImpl } from './contactEditModal/lib/gmailPeopleSearch';
import { handleSelectGmailPerson as handleSelectGmailPersonImpl } from './contactEditModal/lib/gmailPeopleSelect';
import { handleFreeformFind as freeformFindImpl } from './contactEditModal/lib/freeformLookup';
import { handleSubmit as submitImpl } from './contactEditModal/lib/contactSubmit';
import { syncContactToForm } from './contactEditModal/lib/contactFormInit';
import { useContactForm } from './contactEditModal/hooks/useContactForm';
import {
  type ContactNotesDeps,
  handleAddNote as addNoteImpl,
  handleDeleteNote as deleteNoteImpl,
  handleEditNote as editNoteImpl,
  handleSaveNote as saveNoteImpl,
} from './contactEditModal/lib/contactNotesActions';
import {
  type FollowUpDeps,
  handleSaveFollowUp as saveFollowUpImpl,
  handleCompleteFollowUp as completeFollowUpImpl,
  handleSnoozeFollowUp as snoozeFollowUpImpl,
  handleRemoveFollowUp as removeFollowUpImpl,
  handleQuickFollowUp as quickFollowUpImpl,
} from './contactEditModal/lib/contactFollowUpActions';
import {
  fetchRelationshipState,
  handleAnalyze as analyzeImpl,
  handleDraftAction as draftActionImpl,
} from './contactEditModal/lib/relationshipAssistActions';
import { loadEmailInsights, loadPublicActivity } from './contactEditModal/lib/contactTabData';
import { fetchGmailThreads, tryExpandThread } from './contactEditModal/lib/gmailThreadLoader';
import {
  handleEnrich as enrichImpl,
  handleSaveEnrichment as saveEnrichmentImpl,
} from './contactEditModal/lib/contactEnrichmentActions';

interface ContactEditModalProps {
  contact?: ContactObject | null;
  isNew?: boolean;
  isSaving?: boolean;
  viewOnly?: boolean; // True when viewing from Activity Feed (no editing)
  columnColor?: string; // Background color for the form container (from CRM column)
  module?: any; // Module object with config containing labels
  onClose: () => void;
  onSave: (data: Partial<ContactObject>) => void;
  onDelete?: (id: string) => void | Promise<void>;
  onArchive?: (id: string) => void;
  onImportVCF?: () => void;
  onUpdateModuleLabels?: (labels: Array<{ name: string; color: string }>) => Promise<void>; // Callback to update module labels
  columns?: CRMColumnConfig[]; // Pipeline columns for stage selector
  initialStage?: string; // Stage to set when creating from a pipeline column
  showDealValue?: boolean;
  showProbability?: boolean;
  existingContacts?: Array<{ id: string; emails?: string[]; email?: string; phones?: string[]; phone?: string; organization?: string; _etag?: string; version?: number; [key: string]: any }>; // All contacts for duplicate detection during freeform creation
  initialExpandThreadId?: string; // Open the Activity tab and pre-expand this Gmail thread
  listDefs?: ContactListDef[]; // Available list definitions for the per-contact Lists chips field
}


export function ContactEditModal({ contact, isNew, isSaving, viewOnly = false, columnColor, module, onClose, onSave, onDelete, onArchive, onImportVCF, onUpdateModuleLabels, columns = [], initialStage, showDealValue = false, showProbability = false, existingContacts = [], initialExpandThreadId, listDefs = [] }: ContactEditModalProps) {
  // Track if we're editing a note (used by escape key handler)
  const isEditingNoteRef = useRef(false);
  const isMobile = useIsCompactLayout();

  // Get current user for note authorship
  const { profile } = useSession();

  // Safe close (escape-layer contract, issue #3938): if the form has unsaved
  // edits, route the close through the unsaved-changes confirmation instead of
  // discarding silently. isDirtyRef is set by markDirty (wired to the form body's
  // onInput/onChange) and by the email-send handlers. A clean form closes
  // directly. NOTE: setShowUnsavedConfirmation / isDirtyRef are declared lower in
  // the component; this callback only reads them at call time, so forward use is
  // safe (closure captures, not hoisted reads).
  const handleSafeClose = useCallback(() => {
    if (isDirtyRef.current) {
      setShowUnsavedConfirmation(true);
      return;
    }
    onClose();
  }, [onClose]);

  // Close on escape key (but not when editing a note)
  useEscapeKey(() => {
    if (isEditingNoteRef.current) {
      // Don't close modal, let the note editing handle escape
      return;
    }
    handleSafeClose();
  });

  // Handle overlay clicks with buffer zone to prevent accidental closures during copy/paste
  // Also prevent closing when editing a note
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(() => {
    if (!isEditingNoteRef.current) {
      handleSafeClose();
    }
  }, 30);
  const [deleteModalContentRef, handleDeleteOverlayClick, handleDeleteOverlayMouseDown] = useModalOverlayClick(() => setDeleteConfirmation(null), 30);
  const [completeModalContentRef, handleCompleteOverlayClick, handleCompleteOverlayMouseDown] = useModalOverlayClick(() => setShowCompleteConfirmation(false), 30);

  // Hide mobile header/footer when modal is open via body class
  useEffect(() => {
    document.body.classList.add('modal-open');

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  // Edit/create form-field state cluster + mobile-keyboard effect — owned by
  // useContactForm (a single contiguous relocation; hook-call order unchanged).
  // The contact→form init effect (below) still lives here, delegating to
  // syncContactToForm.
  const form = useContactForm({ isNew, contact, isMobile });
  const {
    isViewMode, setIsViewMode, freeformMode, setFreeformMode, freeformText, setFreeformText,
    freeformParsing, setFreeformParsing, freeformParsed, setFreeformParsed,
    gmailResults, setGmailResults, googleContactsRef, gmailSearchTimer,
    firstName, setFirstName, lastName, setLastName, xHandle, setXHandle,
    name, setName, displayNameOverride, setDisplayNameOverride, email, setEmail,
    additionalEmails, setAdditionalEmails,
    phone, setPhone, company, setCompany, lists, setLists, stage, setStage, summary, setSummary,
    dealValue, setDealValue, probability, setProbability, activities, setActivities,
    optimisticSendRef, label, setLabel, labelColor, setLabelColor,
    nextStepAction, setNextStepAction, nextStepDueAt, setNextStepDueAt,
    lastSavedFollowUp, setLastSavedFollowUp,
    relationshipState, setRelationshipState, relStateLoading, setRelStateLoading,
    analyzing, setAnalyzing, draftVariants, setDraftVariants, draftLoading, setDraftLoading,
    showDraftSelector, setShowDraftSelector, composeFromDraft, setComposeFromDraft,
    showMobileActivity, setShowMobileActivity, mobileKeyboardHeight,
    rightTab, setRightTab, publicActivity, setPublicActivity,
    publicActivityLoading, setPublicActivityLoading,
    emailInsights, setEmailInsights, emailInsightsLoading, setEmailInsightsLoading,
    linkedinUrl, setLinkedinUrl, contactTitle, setContactTitle, firm, setFirm,
    location, setLocation, estimatedAum, setEstimatedAum,
    accreditedStatus, setAccreditedStatus, contactSource, setContactSource,
    enrichmentTags, setEnrichmentTags,
    enrichLoading, setEnrichLoading, enrichUnsaved, setEnrichUnsaved,
    enrichHintsPrompt, setEnrichHintsPrompt, enrichHints, setEnrichHints,
    setEnrichResult, setEnrichError,
    enrichmentSummary, setEnrichmentSummary, enrichmentSkills, setEnrichmentSkills,
    enrichmentConfidence, setEnrichmentConfidence, enrichmentAttemptedAt, setEnrichmentAttemptedAt,
    doNotBulkEnrichLocal, setDoNotBulkEnrichLocal,
    enrichmentReviewedAtLocal, setEnrichmentReviewedAtLocal,
  } = form;

  // Notes editing (renamed from Activity log)
  const [newNote, setNewNote] = useState('');
  const [composerFocused, setComposerFocused] = useState(false);
  const [noteType, setNoteType] = useState<'note' | 'call' | 'email' | 'meet'>('email');
  // Composer mode: null = collapsed (just the Reply/Compose/Note buttons).
  // 'reply' = into the last thread (subject inherited), 'compose' = new thread
  // (subject + account picker), 'note' = private internal note.
  const [composerMode, setComposerMode] = useState<'reply' | 'compose' | 'note' | null>(null);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeFrom, setComposeFrom] = useState<string>('');
  const [showComposeEmail, setShowComposeEmail] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);
  const emailFileInputRef = useRef<HTMLInputElement>(null);
  const mobileEmailFileInputRef = useRef<HTMLInputElement>(null);
  const [emailAttachments, setEmailAttachments] = useState<EmailAttachment[]>([]);
  const handleEmailFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => handleEmailFileSelectImpl(e, setEmailAttachments);
  const removeEmailAttachment = (id: string) => removeEmailAttachmentImpl(id, setEmailAttachments);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  type GmailAttachment = { filename: string; mimeType: string; size?: number; attachmentId: string };
  const [gmailMessages, setGmailMessages] = useState<Array<{
    id: string;
    from: string;
    to: string;
    date: string;
    body: string;
    subject: string;
    threadId: string;
    attachments?: GmailAttachment[];
  }>>([]);
  const [gmailLoadAll, setGmailLoadAll] = useState(false);
  const [syncingHistory, setSyncingHistory] = useState(false);
  const [historySynced, setHistorySynced] = useState(false);
  const [gmailSearching, setGmailSearching] = useState(false);
  const [expandedEmail, setExpandedEmail] = useState<{ id: string; from: string; to: string; date: string; body: string; subject: string; attachments?: GmailAttachment[] } | null>(null);
  const [pendingSendId, setPendingSendId] = useState<string | null>(null);
  const [sentMessageIds, setSentMessageIds] = useState<Set<string>>(new Set());
  const [gmailAccountEmails, setGmailAccountEmails] = useState<string[]>([]);
  const refetchGmailRef = useRef<(() => void) | null>(null);

  // Fetch connected Gmail account emails
  useEffect(() => {
    const fetchGmailAccounts = async () => {
      try {
        const authToken = getAuthItem('xavior_auth_token');
        const res = await fetch(`${emailApiBase()}/status`, {
          headers: {
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.accounts) {
            setGmailAccountEmails(data.accounts.map((a: any) => a.email).filter(Boolean));
          } else if (data.account) {
            setGmailAccountEmails([data.account]);
          }
        }
      } catch { /* best-effort */ }
    };
    fetchGmailAccounts();
  }, []);

  // Fetch Gmail messages when contact has email — body lives in
  // contactEditModal/lib/gmailThreadLoader.ts. Effect shell keeps the early
  // return, the interval/refetch-ref lifecycle wiring, and its exact
  // [email, gmailLoadAll, gmailAccountEmails.join(',')] dependency array.
  useEffect(() => {
    if (!email) return;
    const fetchEmails = () => fetchGmailThreads({
      email, contact, gmailLoadAll,
      profileEmail: profile?.email,
      gmailAccountEmails,
      setGmailSearching, setGmailMessages,
    });

    refetchGmailRef.current = fetchEmails;
    fetchEmails();
    const interval = setInterval(fetchEmails, 30000);
    return () => { clearInterval(interval); refetchGmailRef.current = null; };
  }, [email, gmailLoadAll, gmailAccountEmails.join(',')]);

  // Backfill this contact's full mail history on demand: searches the provider
  // for all mail to/from the contact (older than the sync window), caches the
  // headers, then reloads the conversation showing everything.
  const syncFullHistory = async () => {
    if (!email || syncingHistory) return;
    setSyncingHistory(true);
    try {
      const emails = [email, ...additionalEmails].map((e) => (e || '').trim().toLowerCase()).filter(Boolean);
      await http.post(`${emailApiPath()}/backfill-contact`, { email, emails });
      setGmailLoadAll(true);
      setHistorySynced(true);
      refetchGmailRef.current?.();
    } catch {
      /* non-fatal — leave the conversation as-is on failure */
    } finally {
      setSyncingHistory(false);
    }
  };

  // A full-history sync is per-contact; reset the "synced" state when the
  // conversation switches to a different contact so the button reappears.
  useEffect(() => { setHistorySynced(false); }, [email]);

  // Auto-expand a specific email when opened from the Convos view.
  // Convos dispatches xavior:contacts-open-thread with { contactId,
  // threadId }; we expand immediately if gmailMessages is already
  // loaded, otherwise stash the threadId and let the gmailMessages
  // effect below pick it up once the fetch completes.
  const pendingThreadIdRef = useRef<string | null>(initialExpandThreadId ?? null);
  const gmailMessagesRef = useRef(gmailMessages);
  gmailMessagesRef.current = gmailMessages;
  // If the parent passes/changes initialExpandThreadId, seed it.
  useEffect(() => {
    if (initialExpandThreadId) {
      pendingThreadIdRef.current = initialExpandThreadId;
      setRightTab('activity');
      // Try immediately in case messages already loaded.
      tryExpand(initialExpandThreadId);
    }
  }, [initialExpandThreadId]);
  const tryExpand = (tid: string) =>
    tryExpandThread(tid, { gmailMessages: gmailMessagesRef.current, setExpandedEmail });
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (!detail.threadId) return;
      if (detail.contactId && contact && detail.contactId !== contact.id) return;
      setRightTab('activity');
      // Try right now (messages already loaded) — fall back to the
      // pending ref so the gmailMessages effect picks it up later.
      if (!tryExpand(detail.threadId)) {
        pendingThreadIdRef.current = detail.threadId;
      }
    };
    window.addEventListener('xavior:contacts-open-thread', handler);
    return () => window.removeEventListener('xavior:contacts-open-thread', handler);
  }, [contact]);

  useEffect(() => {
    if (!pendingThreadIdRef.current || gmailMessages.length === 0) return;
    if (tryExpand(pendingThreadIdRef.current)) {
      pendingThreadIdRef.current = null;
    }
  }, [gmailMessages]);

  // Label dropdown state
  const [showLabelDropdown, setShowLabelDropdown] = useState(false);
  const [customLabelInput, setCustomLabelInput] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(DEFAULT_LABEL_COLOR);
  const [labelDropdownPosition, setLabelDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const labelButtonRef = useRef<HTMLButtonElement>(null);
  const labelDropdownRef = useRef<HTMLDivElement>(null);
  const labelColors = LABEL_COLORS;

  // Get labels from module config
  const moduleLabels = (module?.config?.labels || []) as Array<{ name: string; color: string }>;
  const existingLabels = moduleLabels;

  // State for delete button on hover
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const [showDeleteButton, setShowDeleteButton] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<number | null>(null);

  // UI state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ contactId: string; name: string } | null>(null);
  const [showCompleteConfirmation, setShowCompleteConfirmation] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const headerMenuRef = useRef<HTMLDivElement>(null);
  const [showUnsavedConfirmation, setShowUnsavedConfirmation] = useState(false);
  const isDirtyRef = useRef(false);
  const markDirty = useCallback(() => { isDirtyRef.current = true; }, []);
  const [nextFollowUpSchedule, setNextFollowUpSchedule] = useState<'none' | '1week' | '2weeks' | '1month' | 'custom'>('none');
  const [nextFollowUpDate, setNextFollowUpDate] = useState(() => {
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + 7);
    return nextDate.toISOString().split('T')[0];
  });
  const [nextFollowUpTitle, setNextFollowUpTitle] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerViewDate, setDatePickerViewDate] = useState(new Date());
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [isEditingFollowUp, setIsEditingFollowUp] = useState(false);
  const [showStageDropdown, setShowStageDropdown] = useState(false);
  const stageDropdownRef = useRef<HTMLDivElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);

  // Gmail people search — debounced as user types
  const searchGmailPeople = useCallback(async (query: string) => {
    await searchGmailPeopleImpl(query, { setGmailResults, googleContactsRef });
  }, []);

  const handleFreeformTextChange = useCallback((text: string) => {
    setFreeformText(text);
    if (gmailSearchTimer.current) clearTimeout(gmailSearchTimer.current);
    // Only search if it looks like a name (no special chars, not too long)
    const trimmed = text.trim();
    if (trimmed.length >= 2 && trimmed.length <= 50 && !/[0-9@.(){}[\]]/.test(trimmed)) {
      gmailSearchTimer.current = setTimeout(() => searchGmailPeople(trimmed), 300);
    } else {
      setGmailResults([]);
    }
  }, [searchGmailPeople]);

  const handleSelectGmailPerson = useCallback((person: { email: string; name: string }) => {
    handleSelectGmailPersonImpl(person, {
      googleContactsRef, setGmailResults, setFirstName, setLastName, setName, setEmail,
      setFreeformText, setFreeformParsed, setCompany, setFirm, setPhone, setContactTitle,
      setEnrichLoading, setXHandle, setLinkedinUrl, setSummary, setLocation,
      setEnrichmentSummary, setEnrichmentSkills, setEnrichmentConfidence,
    });
  }, []);

  // Parse freeform text and optionally enrich — body lives in
  // contactEditModal/lib/freeformLookup.ts. Deps carry the VALUES captured at
  // call time (freeformText, gmailResults, doNotBulkEnrichLocal, summary),
  // preserving the original closure semantics (non-memoized, as before).
  const handleFreeformFind = async () => {
    await freeformFindImpl({
      freeformText, gmailResults, doNotBulkEnrichLocal, summary,
      setFreeformParsing, setFreeformParsed, setFirstName, setLastName, setName, setEmail,
      setPhone, setCompany, setFirm, setContactTitle, setLocation, setEnrichHints, setNewNote,
      setEnrichLoading, setEnrichError, setEnrichResult, setEnrichmentSummary, setEnrichmentSkills,
      setEnrichmentConfidence, setLinkedinUrl, setXHandle, setSummary,
    });
  };

  const handleDealValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatNumberWithCommas(e.target.value);
    setDealValue(formatted);
  };

  // AI enrichment handlers — bodies live in
  // contactEditModal/lib/contactEnrichmentActions.ts. Deps carry the field
  // VALUES captured at call time (not getters), preserving the original closure
  // semantics where post-await blank-fill guards read the snapshotted value.
  const handleEnrich = async (extraHintsArg?: string | unknown) => {
    await enrichImpl(extraHintsArg, {
      firstName, lastName, email, contact, company, firm, location, columns, stage, phone,
      linkedinUrl, summary, contactTitle, setEnrichLoading, setEnrichError, setEnrichResult,
      setEnrichHintsPrompt, setPhone, setCompany, setFirm, setContactTitle, setEnrichmentSummary,
      setEnrichmentSkills, setEnrichmentConfidence, setEnrichmentAttemptedAt, setLocation,
      setLinkedinUrl, setXHandle, setSummary, setEnrichUnsaved,
    });
  };

  const handleSaveEnrichment = () => {
    saveEnrichmentImpl({
      contact, isNew, enrichmentSummary, enrichmentSkills, enrichmentConfidence, enrichmentAttemptedAt,
      contactTitle, linkedinUrl, location, firm, company, summary, xHandle, phone, enrichHints,
      onSave, setEnrichUnsaved,
    });
  };

  // Contact → form-state reset/sync — body lives in
  // contactEditModal/lib/contactFormInit.ts. Effect shell keeps the EXACT
  // [contact, profile?.id, profile?.name] dependency array; the deps object
  // carries the live setters + optimisticSendRef (by reference) and the
  // profile/initialStage values captured at effect-run time.
  useEffect(() => {
    syncContactToForm(contact, {
      profile, initialStage, optimisticSendRef,
      setIsViewMode, setFreeformMode, setFreeformText, setFreeformParsed, setEmailAttachments,
      setFirstName, setLastName, setName, setDisplayNameOverride, setEmail, setAdditionalEmails, setPhone, setCompany,
      setLists, setStage, setXHandle, setSummary, setDealValue, setProbability, setActivities,
      setLabel, setLabelColor, setNextStepAction, setNextStepDueAt, setLastSavedFollowUp,
      setLinkedinUrl, setContactTitle, setFirm, setLocation, setEstimatedAum, setAccreditedStatus,
      setContactSource, setEnrichmentTags, setEnrichmentSummary, setEnrichmentSkills,
      setEnrichmentConfidence, setEnrichmentAttemptedAt, setDoNotBulkEnrichLocal,
      setEnrichmentReviewedAtLocal, setEnrichResult, setEnrichError, setEnrichLoading,
      setEnrichUnsaved, setEnrichHintsPrompt, setEnrichHints, setNewNote, setCustomLabelInput,
      setIsEditingFollowUp,
    });
  }, [contact, profile?.id, profile?.name]);

  // Auto-enrich when opening a contact that has no enrichment data yet.
  // Skipped when the contact has `doNotBulkEnrich: true` (user previously cleared
  // AI data and opted out). The manual "Build AI profile" button bypasses
  // this guard because that's an explicit user action.
  //
  // Fix (#2388/#2436): use isAutoEnrichOptedOut which checks BOTH the
  // top-level `contact.doNotBulkEnrich` (list-endpoint shape) AND the
  // nested `contact.attributes.doNotBulkEnrich` (detail-endpoint shape).
  // Previously only the nested shape was checked, so contacts opened
  // from the list view (where the API flattens attributes) bypassed
  // the opt-out guard. (Conflict resolution after #2554 merge: keeping
  // this PR's helper-based approach, which is the DRY version of
  // develop's inline check.)
  useEffect(() => {
    const optedOut = contact ? isAutoEnrichOptedOut(contact) : false;
    if (contact && !isNew && !optedOut && !isAlreadyEnriched(contact as unknown as EnrichmentContact) && !enrichLoading && firstName.trim()) {
      // Small delay to let the modal render first. The cancelled flag prevents
      // handleEnrich from firing if the modal unmounts during the delay.
      let cancelled = false;
      const timer = setTimeout(() => { if (!cancelled) handleEnrich(); }, 500);
      return () => { cancelled = true; clearTimeout(timer); };
    }
  }, [contact?.id]); // Only run when contact changes, not on every render

  // Listen for discover-add events — trigger full enrichment flow
  useEffect(() => {
    const handler = (e: Event) => {
      const { email, name } = (e as CustomEvent).detail;
      if (!email || !name) return;
      handleSelectGmailPerson({ email, name });
    };
    window.addEventListener('xavior:discover-add', handler);
    return () => window.removeEventListener('xavior:discover-add', handler);
  }, [handleSelectGmailPerson]);

  // Close stage dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (stageDropdownRef.current && !stageDropdownRef.current.contains(event.target as Node)) {
        setShowStageDropdown(false);
      }
    };

    if (showStageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showStageDropdown]);

  // Update label dropdown position when opened
  useEffect(() => {
    if (showLabelDropdown && labelButtonRef.current) {
      const rect = labelButtonRef.current.getBoundingClientRect();
      setLabelDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [showLabelDropdown]);

  // Close label dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (labelDropdownRef.current && !labelDropdownRef.current.contains(event.target as Node)) {
        setShowLabelDropdown(false);
        // Clear hover states when dropdown closes
        setHoveredLabel(null);
        setShowDeleteButton(null);
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      }
    }

    if (showLabelDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLabelDropdown]);

  // Fetch cached relationship state when modal opens — body lives in
  // contactEditModal/lib/relationshipAssistActions.ts. Effect shell + its
  // [contact?.id, isNew] dependency array stay here unchanged.
  useEffect(() => {
    if (!contact?.id || isNew) return;
    fetchRelationshipState({ contactId: contact.id, setRelStateLoading, setRelationshipState });
  }, [contact?.id, isNew]);

  const handleAnalyze = async () => {
    await analyzeImpl({ contactId: contact?.id, setAnalyzing, setRelationshipState });
  };

  const handleDraftAction = async (actionType: string) => {
    await draftActionImpl(actionType, { contactId: contact?.id, setDraftLoading, setShowDraftSelector, setDraftVariants });
  };

  const handleSelectDraftVariant = (variant: DraftVariant) => {
    setShowDraftSelector(false);
    setComposeFromDraft({ subject: variant.subject, body: variant.body });
  };

  const handleStageChange = (newStage: string) => {
    setStage(newStage);
    setShowStageDropdown(false);
    if (!isNew && contact) {
      onSave({ stage: newStage });
    }
  };

  // Activity log handlers — bodies live in contactEditModal/lib/contactNotesActions.ts.
  // Deps are rebuilt per-call so each handler reads the latest state snapshot
  // (these are plain functions, not memoized, exactly as before).
  const getNotesDeps = (): ContactNotesDeps => ({
    activities, setActivities, newNote, setNewNote, noteType, setNoteType,
    editingNoteId, setEditingNoteId, editingNoteText, setEditingNoteText,
    isEditingNoteRef, profile, isNew, contact, onSave,
  });
  const handleAddNote = () => { addNoteImpl(getNotesDeps()); };

  // Send the current composer body as an email with the given subject line, then
  // collapse the composer. Extracted from the inline composers so Reply (inherited
  // subject) and Compose (typed subject) — on both desktop and mobile — share one
  // optimistic-send path with rollback. Mirrors the prior inline behavior exactly.
  const sendContactEmail = (subjectLine: string) => {
    const recipientEmail = email || contact?.emails?.[0] || contact?.email;
    if (!recipientEmail) { alert('No email address for this contact'); return; }
    if (!newNote.trim()) return;
    const text = newNote.trim();
    const attachmentNames = emailAttachments.map(a => a.filename);
    const emailActivity: Activity = {
      id: `email-${Date.now()}`,
      date: new Date().toISOString(),
      note: `Email to ${recipientEmail}: ${text}${attachmentNames.length > 0 ? `\n[Attachments: ${attachmentNames.join(', ')}]` : ''}`,
      authorId: profile?.id || '',
      authorName: profile?.name || 'Unknown',
      type: 'email',
    };
    const previousActivities = [...activities];
    const updated = [emailActivity, ...activities];
    setActivities(updated);
    setNewNote('');
    setComposeSubject('');
    setComposerMode(null);
    isDirtyRef.current = true;
    onSave({ notes: activitiesToNotes(updated), activities: updated } as any);
    optimisticSendRef.current = true;
    setPendingSendId(emailActivity.id);
    const authToken = getAuthItem('xavior_auth_token');
    const sendAttachments = emailAttachments.length > 0 ? emailAttachments.map(a => ({ filename: a.filename, mimeType: a.mimeType, data: a.data })) : undefined;
    setEmailAttachments([]);
    fetch(`${emailApiBase()}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
      body: JSON.stringify({
        to: recipientEmail,
        subject: subjectLine.trim() || `Message from ${profile?.name || 'Xavior'}`,
        body: text,
        ...(composeFrom ? { from: composeFrom } : {}),
        ...(sendAttachments ? { attachments: sendAttachments } : {}),
      }),
    }).then(res => {
      optimisticSendRef.current = false;
      setPendingSendId(null);
      if (!res.ok) {
        setActivities(previousActivities);
        onSave({ notes: activitiesToNotes(previousActivities), activities: previousActivities } as any);
        alert('Email failed to send');
      } else {
        setSentMessageIds(prev => new Set(prev).add(emailActivity.id));
        setTimeout(() => refetchGmailRef.current?.(), 2000);
      }
    }).catch(() => {
      optimisticSendRef.current = false;
      setPendingSendId(null);
      setActivities(previousActivities);
      onSave({ notes: activitiesToNotes(previousActivities), activities: previousActivities } as any);
      alert('Email failed to send');
    });
  };

  // Mobile composer send — lifted VERBATIM from the mobile bottom-sheet's inline
  // onClick send body (keeps the 3rd raw fetch on this orchestrator so the new
  // ContactEditMobileActivitySheet leaf has ZERO fetch). NOT identical to
  // sendContactEmail: builds a `type: 'email-sent'` activity with a `temp-` id
  // and uses try/catch (not .then). Passed to the mobile sheet as its onSend.
  const sendContactEmailMobile = async () => {
    const recipientEmail = email || contact?.emails?.[0];
    if (!recipientEmail || !newNote.trim()) return;
    const previousActivities = [...activities];
    const mobileAttachmentNames = emailAttachments.map(a => a.filename);
    const emailActivity = {
      id: `temp-${Date.now()}`,
      type: 'email-sent' as const,
      note: `${newNote.trim()}${mobileAttachmentNames.length > 0 ? `\n[Attachments: ${mobileAttachmentNames.join(', ')}]` : ''}`,
      date: new Date().toISOString(),
    };
    const updated = [emailActivity, ...activities];
    setActivities(updated);
    setNewNote('');
    setComposeSubject('');
    setComposerMode(null);
    isDirtyRef.current = true;
    onSave({ notes: activitiesToNotes(updated), activities: updated } as any);
    try {
      const authToken = getAuthItem('xavior_auth_token');
      const mobileSendAttachments = emailAttachments.length > 0 ? emailAttachments.map(a => ({ filename: a.filename, mimeType: a.mimeType, data: a.data })) : undefined;
      setEmailAttachments([]);
      const res = await fetch(`${emailApiBase()}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          to: recipientEmail,
          subject: (composerMode === 'compose' ? composeSubject.trim() : composerLastSubject) || `Message from ${profile?.name || 'Xavior'}`,
          body: emailActivity.note,
          ...(composeFrom ? { from: composeFrom } : {}),
          ...(mobileSendAttachments ? { attachments: mobileSendAttachments } : {}),
        }),
      });
      if (!res.ok) {
        setActivities(previousActivities);
        onSave({ notes: activitiesToNotes(previousActivities), activities: previousActivities } as any);
        alert('Email failed to send');
      } else { setTimeout(() => refetchGmailRef.current?.(), 2000); }
    } catch {
      setActivities(previousActivities);
      onSave({ notes: activitiesToNotes(previousActivities), activities: previousActivities } as any);
      alert('Email failed to send');
    }
  };

  // Reply context for the composer: the most-recent thread's subject (so Reply
  // inherits it) and whether any thread exists at all (Reply only shows then).
  const composerLastSubject = (() => {
    if (gmailMessages.length === 0) return '';
    const latest = gmailMessages.reduce((a, b) =>
      new Date(b.date).getTime() > new Date(a.date).getTime() ? b : a);
    const s = (latest.subject || '').trim();
    if (!s) return '';
    return /^re:/i.test(s) ? s : `Re: ${s}`;
  })();
  // Reply is offered ONLY when the contact has the most-recent message — you
  // shouldn't "reply" to your own last email/reply. Compares the newest message
  // FROM the contact (cached thread) against your newest message (cached sent
  // OR a just-sent optimistic 'email' activity); Reply shows only if theirs wins.
  const composerCanReply = (() => {
    if (gmailMessages.length === 0) return false;
    const userLcs = [...new Set([profile?.email, ...gmailAccountEmails].filter(Boolean))].map((e) => (e as string).toLowerCase());
    const contactLc = (email || '').toLowerCase();
    const received = gmailMessages.filter((m) => !determineEmailDirection((m.from || '').toLowerCase(), userLcs, contactLc || undefined));
    if (received.length === 0) return false;
    const newestReceived = received.reduce((max, m) => Math.max(max, new Date(m.date).getTime()), 0);
    const newestSentGmail = gmailMessages
      .filter((m) => determineEmailDirection((m.from || '').toLowerCase(), userLcs, contactLc || undefined))
      .reduce((max, m) => Math.max(max, new Date(m.date).getTime()), 0);
    const newestSentActivity = activities
      .filter((a) => a.type === 'email')
      .reduce((max, a) => Math.max(max, new Date(a.date).getTime()), 0);
    return newestReceived > Math.max(newestSentGmail, newestSentActivity);
  })();
  const connectedMailboxes = [...new Set([...gmailAccountEmails, profile?.email].filter(Boolean))] as string[];

  // Follow-up scheduler handlers — bodies live in
  // contactEditModal/lib/contactFollowUpActions.ts. Deps rebuilt per-call so
  // each handler reads the latest state snapshot (non-memoized, as before).
  const getFollowUpDeps = (): FollowUpDeps => ({
    nextStepDueAt, nextStepAction, isNew, contact, activities, setActivities, profile,
    setNextStepAction, setNextStepDueAt, setLastSavedFollowUp,
    nextFollowUpSchedule, nextFollowUpDate, nextFollowUpTitle,
    setNextFollowUpSchedule, setNextFollowUpDate, setNextFollowUpTitle, onSave,
  });
  const handleSaveFollowUp = () => { saveFollowUpImpl(getFollowUpDeps()); };
  const handleCompleteFollowUp = () => { completeFollowUpImpl(getFollowUpDeps()); };
  const handleSnoozeFollowUp = () => { snoozeFollowUpImpl(getFollowUpDeps()); };
  const handleRemoveFollowUp = () => { removeFollowUpImpl(getFollowUpDeps()); };
  const handleQuickFollowUp = (offset: { days?: number; months?: number }) => { quickFollowUpImpl(offset, getFollowUpDeps()); };

  const handleDeleteNote = (id: string) => { deleteNoteImpl(id, getNotesDeps()); };

  const handleEditNote = (activity: Activity) => { editNoteImpl(activity, getNotesDeps()); };

  const handleSaveNote = () => { saveNoteImpl(getNotesDeps()); };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
    // Delay resetting the ref to allow overlay click handler to check it first
    setTimeout(() => {
      isEditingNoteRef.current = false;
    }, 100);
  };

  // Form-submit persistence — body lives in contactEditModal/lib/contactSubmit.ts.
  // Kept as a plain (non-memoized) function so the [handleSubmit] dep of the
  // Enter-key effect still changes identity every render exactly as before.
  // Deps carry the field VALUES captured at call time (closure semantics
  // preserved: the follow-up/attributes branches read snapshotted values).
  const handleSubmit = (e: React.FormEvent) => {
    submitImpl(e, {
      firstName, lastName, activities, isNew, nextStepDueAt, nextStepAction, lastSavedFollowUp,
      profile, email, additionalEmails, phone, stage, label, labelColor, company, lists, summary, dealValue,
      probability, contact, xHandle, linkedinUrl, contactTitle, firm, location, enrichmentSummary,
      enrichmentSkills, enrichmentConfidence, enrichmentAttemptedAt, estimatedAum, accreditedStatus,
      contactSource, enrichmentTags, enrichHints,
      setActivities, setLastSavedFollowUp, setEnrichmentSummary, setEnrichmentSkills,
      setEnrichmentConfidence, setContactTitle, setLocation, setLinkedinUrl, setSummary, setFirm,
      onSave,
    });
  };

  // Global Enter key shortcut to save when not focused on input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isTyping = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT'
      );

      if (e.key === 'Enter' && !isTyping) {
        e.preventDefault();
        handleSubmit(e as any);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSubmit]);

  // Render complete follow-up confirmation modal
  // Complete follow-up confirmation modal — now a props-driven leaf:
  //   contactEditModal/components/CompleteFollowUpModal.tsx (rendered below).

  // --- Activity feed helpers ---
  const [activeTab, setActiveTab] = useState<'activity' | 'emails'>('activity');

  // Group activities by month
  const groupedActivities = groupActivitiesByMonth(activities);

  // Engagement chip computation. Combine note-style activities AND
  // Gmail messages exchanged with this contact — without the email
  // axis, contacts you only correspond with via email always read as
  // "No interactions" even when there's a fresh thread on file.
  const lastNoteDate = activities.length > 0
    ? activities.reduce((latest, a) => new Date(a.date) > new Date(latest.date) ? a : latest).date
    : null;
  const lastEmailDate = gmailMessages.length > 0
    ? gmailMessages.reduce((latest, m) => new Date(m.date).getTime() > new Date(latest.date).getTime() ? m : latest).date
    : null;
  const lastInteractionTs = (() => {
    const tsN = lastNoteDate ? new Date(lastNoteDate).getTime() : 0;
    const tsE = lastEmailDate ? new Date(lastEmailDate).getTime() : 0;
    return Math.max(tsN, tsE) || null;
  })();
  const daysSince = lastInteractionTs ? Math.floor((Date.now() - lastInteractionTs) / 86400000) : null;
  // Drop the raw `${score}/100` — it was just `100 - daysSince`, a
  // proxy nobody could interpret at a glance. Show the days-since-
  // last-interaction directly, which is what the score stood in for.
  const engagementText = daysSince !== null
    ? (daysSince === 0 ? 'Today' : daysSince === 1 ? 'Yesterday' : `${daysSince}d ago`)
    : 'No interactions';

  // Avatar helpers — use shared color ramp so kanban and detail views match
  const avatarColor = getAvatarColorPair(name || `${firstName} ${lastName}`.trim());
  const getInitials = (fn: string, ln: string) => {
    return getSharedInitials(fn, ln) || '?';
  };

  const modalContent = (
    <div
      className="edit-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: isMobile ? '#fff' : 'rgba(0, 0, 0, 0.7)',
        zIndex: 2147483647,
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 16,
        pointerEvents: 'auto',
      }}
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalContentRef}
        className="edit-modal-content"
        style={{
          background: 'var(--color-surface)',
          borderRadius: isMobile ? 0 : 16,
          width: '100%',
          maxWidth: isMobile ? '100%' : (freeformMode ? 720 : 1100),
          height: isMobile ? '100%' : (freeformMode ? 'auto' : 'min(88vh, 720px)'),
          minHeight: isMobile ? undefined : (freeformMode ? 420 : undefined),
          maxHeight: isMobile ? '100%' : (freeformMode ? '88vh' : undefined),
          display: 'grid',
          gridTemplateColumns: (isMobile || freeformMode) ? '1fr' : '1fr 420px',
          overflow: 'hidden',
          boxShadow: isMobile ? 'none' : '0 24px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.06)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ==================== LEFT COLUMN ==================== */}
        <div
          ref={leftPanelRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden',
            flex: 1,
          }}>
          <ContactEditHeaderBar
            isMobile={isMobile}
            viewOnly={viewOnly}
            isNew={isNew}
            isSaving={isSaving}
            freeformMode={freeformMode}
            isViewMode={isViewMode}
            setIsViewMode={setIsViewMode}
            onClose={onClose}
            contact={contact}
            columns={columns}
            stage={stage}
            showStageDropdown={showStageDropdown}
            setShowStageDropdown={setShowStageDropdown}
            stageDropdownRef={stageDropdownRef}
            handleStageChange={handleStageChange}
            showHeaderMenu={showHeaderMenu}
            setShowHeaderMenu={setShowHeaderMenu}
            headerMenuRef={headerMenuRef}
            onArchive={onArchive}
            onDelete={onDelete}
            showArchiveConfirm={showArchiveConfirm}
            setShowArchiveConfirm={setShowArchiveConfirm}
            setDeleteConfirmation={setDeleteConfirmation}
            enrichLoading={enrichLoading}
            handleEnrich={handleEnrich}
            name={name}
            firstName={firstName}
            lastName={lastName}
            displayNameOverride={displayNameOverride}
            contactTitle={contactTitle}
            company={company}
            firm={firm}
            email={email}
            phone={phone}
            activities={activities}
            engagementText={engagementText}
            relationshipState={relationshipState}
            doNotBulkEnrichLocal={doNotBulkEnrichLocal}
            setDoNotBulkEnrichLocal={setDoNotBulkEnrichLocal}
            avatarColor={avatarColor}
            getInitials={getInitials}
            onSave={onSave}
            setName={setName}
            summary={summary}
            linkedinUrl={linkedinUrl}
            location={location}
            enrichmentSummary={enrichmentSummary}
            enrichmentSkills={enrichmentSkills}
            enrichmentConfidence={enrichmentConfidence}
            enrichmentAttemptedAt={enrichmentAttemptedAt}
            enrichHints={enrichHints}
            setEnrichmentSummary={setEnrichmentSummary}
            setEnrichmentSkills={setEnrichmentSkills}
            setEnrichmentConfidence={setEnrichmentConfidence}
            setEnrichmentAttemptedAt={setEnrichmentAttemptedAt}
            setContactTitle={setContactTitle}
            setLocation={setLocation}
            setLinkedinUrl={setLinkedinUrl}
            setSummary={setSummary}
            setFirm={setFirm}
            markDirty={markDirty}
            scrollBody={
              /* ---- FREEFORM MODE (new contacts) ---- */
              (freeformMode || viewOnly || isViewMode) ? (
                <ContactEditFreeformPanel
                  freeformMode={freeformMode}
                  isMobile={isMobile}
                  freeformText={freeformText}
                  handleFreeformTextChange={handleFreeformTextChange}
                  gmailResults={gmailResults}
                  setGmailResults={setGmailResults}
                  handleSelectGmailPerson={handleSelectGmailPerson}
                  handleFreeformFind={handleFreeformFind}
                  freeformParsing={freeformParsing}
                  freeformParsed={freeformParsed}
                  setFreeformParsed={setFreeformParsed}
                  firstName={firstName}
                  setFirstName={setFirstName}
                  lastName={lastName}
                  setLastName={setLastName}
                  name={name}
                  setName={setName}
                  email={email}
                  setEmail={setEmail}
                  phone={phone}
                  setPhone={setPhone}
                  company={company}
                  setCompany={setCompany}
                  firm={firm}
                  setFirm={setFirm}
                  contactTitle={contactTitle}
                  setContactTitle={setContactTitle}
                  location={location}
                  setLocation={setLocation}
                  linkedinUrl={linkedinUrl}
                  setLinkedinUrl={setLinkedinUrl}
                  xHandle={xHandle}
                  setXHandle={setXHandle}
                  summary={summary}
                  setSummary={setSummary}
                  stage={stage}
                  setFreeformMode={setFreeformMode}
                  setIsViewMode={setIsViewMode}
                  enrichmentSummary={enrichmentSummary}
                  setEnrichmentSummary={setEnrichmentSummary}
                  enrichmentSkills={enrichmentSkills}
                  setEnrichmentSkills={setEnrichmentSkills}
                  enrichmentConfidence={enrichmentConfidence}
                  setEnrichmentConfidence={setEnrichmentConfidence}
                  enrichmentAttemptedAt={enrichmentAttemptedAt}
                  setEnrichmentAttemptedAt={setEnrichmentAttemptedAt}
                  enrichmentTags={enrichmentTags}
                  enrichLoading={enrichLoading}
                  enrichUnsaved={enrichUnsaved}
                  setEnrichUnsaved={setEnrichUnsaved}
                  enrichHintsPrompt={enrichHintsPrompt}
                  setEnrichHintsPrompt={setEnrichHintsPrompt}
                  enrichHints={enrichHints}
                  setEnrichHints={setEnrichHints}
                  setEnrichResult={setEnrichResult}
                  setEnrichmentReviewedAtLocal={setEnrichmentReviewedAtLocal}
                  enrichmentReviewedAtLocal={enrichmentReviewedAtLocal}
                  handleEnrich={handleEnrich}
                  handleSaveEnrichment={handleSaveEnrichment}
                  contact={contact}
                  isNew={isNew}
                  label={label}
                  labelColor={labelColor}
                  showDealValue={showDealValue}
                  showProbability={showProbability}
                  dealValue={dealValue}
                  probability={probability}
                  setShowMobileActivity={setShowMobileActivity}
                  onSave={onSave}
                />
              ) : (
                /* ---- EDIT MODE ---- */
                <ContactEditFormBody
                  handleSubmit={handleSubmit}
                  columnColor={columnColor}
                  contact={contact}
                  isNew={isNew}
                  isSaving={isSaving}
                  onClose={onClose}
                  onArchive={onArchive}
                  profile={profile}
                  firstName={firstName}
                  setFirstName={setFirstName}
                  lastName={lastName}
                  setLastName={setLastName}
                  email={email}
                  setEmail={setEmail}
                  additionalEmails={additionalEmails}
                  setAdditionalEmails={setAdditionalEmails}
                  phone={phone}
                  setPhone={setPhone}
                  company={company}
                  setCompany={setCompany}
                  xHandle={xHandle}
                  setXHandle={setXHandle}
                  lists={lists}
                  setLists={setLists}
                  listDefs={listDefs}
                  enrichmentSummary={enrichmentSummary}
                  enrichHints={enrichHints}
                  setEnrichHints={setEnrichHints}
                  showDealValue={showDealValue}
                  dealValue={dealValue}
                  handleDealValueChange={handleDealValueChange}
                  contactTitle={contactTitle}
                  setContactTitle={setContactTitle}
                  firm={firm}
                  setFirm={setFirm}
                  location={location}
                  setLocation={setLocation}
                  linkedinUrl={linkedinUrl}
                  setLinkedinUrl={setLinkedinUrl}
                  contactSource={contactSource}
                  setContactSource={setContactSource}
                  enrichmentTags={enrichmentTags}
                />
              )
            }
          />
        </div>

        {/* ==================== RIGHT COLUMN (desktop) ==================== */}
        {!isMobile && !freeformMode && (
          <ContactEditDesktopTimeline
            onClose={onClose}
            viewOnly={viewOnly}
            rightTab={rightTab}
            emailInsights={emailInsights}
            emailInsightsLoading={emailInsightsLoading}
            publicActivity={publicActivity}
            publicActivityLoading={publicActivityLoading}
            onTabSelect={(tab) => {
              setRightTab(tab);
              if (tab === 'insights' && emailInsights === null && !emailInsightsLoading && gmailMessages.length > 0) {
                loadEmailInsights({
                  gmailMessages,
                  contactName: name || `${firstName} ${lastName}`.trim(),
                  userName: profile?.name || profile?.email,
                  setEmailInsightsLoading,
                  setEmailInsights,
                });
              }
              if (tab === 'public' && publicActivity === null && !publicActivityLoading) {
                loadPublicActivity({
                  contactName: name || `${firstName} ${lastName}`.trim(),
                  xHandle, company, firm, contactTitle, enrichmentSummary,
                  setPublicActivityLoading,
                  setPublicActivity,
                });
              }
            }}
            expandedEmail={expandedEmail}
            setExpandedEmail={setExpandedEmail}
            refetchGmail={() => refetchGmailRef.current?.()}
            gmailMessages={gmailMessages}
            gmailSearching={gmailSearching}
            gmailLoadAll={gmailLoadAll}
            setGmailLoadAll={setGmailLoadAll}
            activities={activities}
            email={email}
            profile={profile}
            gmailAccountEmails={gmailAccountEmails}
            historySynced={historySynced}
            syncingHistory={syncingHistory}
            syncFullHistory={syncFullHistory}
            pendingSendId={pendingSendId}
            sentMessageIds={sentMessageIds}
            editingNoteId={editingNoteId}
            editingNoteText={editingNoteText}
            setEditingNoteText={setEditingNoteText}
            setEditingNoteId={setEditingNoteId}
            setActivities={setActivities}
            isEditingNoteRef={isEditingNoteRef}
            handleSaveNote={handleSaveNote}
            handleCancelEdit={handleCancelEdit}
            handleEditNote={handleEditNote}
            handleDeleteNote={handleDeleteNote}
            composerSlot={
              <ContactEditDesktopComposer
                composerMode={composerMode}
                setComposerMode={setComposerMode}
                composerCanReply={composerCanReply}
                composerLastSubject={composerLastSubject}
                connectedMailboxes={connectedMailboxes}
                noteType={noteType}
                setNoteType={setNoteType}
                composeSubject={composeSubject}
                setComposeSubject={setComposeSubject}
                composeFrom={composeFrom}
                setComposeFrom={setComposeFrom}
                newNote={newNote}
                setNewNote={setNewNote}
                composerFocused={composerFocused}
                setComposerFocused={setComposerFocused}
                emailAttachments={emailAttachments}
                setEmailAttachments={setEmailAttachments}
                emailFileInputRef={emailFileInputRef}
                handleEmailFileSelect={handleEmailFileSelect}
                removeEmailAttachment={removeEmailAttachment}
                aiDrafting={aiDrafting}
                setAiDrafting={setAiDrafting}
                gmailMessages={gmailMessages}
                activities={activities}
                name={name}
                firstName={firstName}
                lastName={lastName}
                email={email}
                company={company}
                firm={firm}
                contactTitle={contactTitle}
                profile={profile}
                handleAddNote={handleAddNote}
                onSend={sendContactEmail}
              />
            }
          />
        )}
      </div>
      <DeleteContactConfirmationModal
        deleteConfirmation={deleteConfirmation}
        contentRef={deleteModalContentRef}
        onOverlayClick={handleDeleteOverlayClick}
        onOverlayMouseDown={handleDeleteOverlayMouseDown}
        onCancel={() => setDeleteConfirmation(null)}
        onDelete={onDelete}
        onClose={onClose}
      />
      <CompleteFollowUpModal
        show={showCompleteConfirmation}
        contactDisplayName={`${firstName} ${lastName}`.trim()}
        nextFollowUpTitle={nextFollowUpTitle}
        setNextFollowUpTitle={setNextFollowUpTitle}
        nextFollowUpSchedule={nextFollowUpSchedule}
        setNextFollowUpSchedule={setNextFollowUpSchedule}
        nextFollowUpDate={nextFollowUpDate}
        setNextFollowUpDate={setNextFollowUpDate}
        showDatePicker={showDatePicker}
        setShowDatePicker={setShowDatePicker}
        datePickerViewDate={datePickerViewDate}
        setDatePickerViewDate={setDatePickerViewDate}
        datePickerRef={datePickerRef}
        completeModalContentRef={completeModalContentRef}
        onOverlayMouseDown={handleCompleteOverlayMouseDown}
        onOverlayClick={handleCompleteOverlayClick}
        onComplete={() => {
          handleCompleteFollowUp();
          setShowCompleteConfirmation(false);
          setNextFollowUpSchedule('none');
          setNextFollowUpDate('');
          setNextFollowUpTitle('');
        }}
        onClose={() => {
          setShowCompleteConfirmation(false);
          setNextFollowUpSchedule('none');
          setNextFollowUpDate('');
          setNextFollowUpTitle('');
        }}
      />

      {/* Draft Variant Selector */}
      <DraftVariantSelector
        isOpen={showDraftSelector}
        onClose={() => setShowDraftSelector(false)}
        variants={draftVariants}
        loading={draftLoading}
        onSelectVariant={handleSelectDraftVariant}
        contactEmail={contact?.email || contact?.emails?.[0]}
      />

      {/* Mobile Activity Bottom Sheet */}
      {/* Mobile Activity Bottom Sheet */}
      {isMobile && showMobileActivity && (
        <ContactEditMobileActivitySheet
          mobileKeyboardHeight={mobileKeyboardHeight}
          setShowMobileActivity={setShowMobileActivity}
          name={name}
          firstName={firstName}
          lastName={lastName}
          activities={activities}
          gmailMessages={gmailMessages}
          profile={profile}
          gmailAccountEmails={gmailAccountEmails}
          composerMode={composerMode}
          setComposerMode={setComposerMode}
          composerCanReply={composerCanReply}
          composerLastSubject={composerLastSubject}
          connectedMailboxes={connectedMailboxes}
          noteType={noteType}
          setNoteType={setNoteType}
          composeSubject={composeSubject}
          setComposeSubject={setComposeSubject}
          composeFrom={composeFrom}
          setComposeFrom={setComposeFrom}
          newNote={newNote}
          setNewNote={setNewNote}
          composerFocused={composerFocused}
          setComposerFocused={setComposerFocused}
          emailAttachments={emailAttachments}
          setEmailAttachments={setEmailAttachments}
          mobileEmailFileInputRef={mobileEmailFileInputRef}
          handleEmailFileSelect={handleEmailFileSelect}
          removeEmailAttachment={removeEmailAttachment}
          handleAddNote={handleAddNote}
          onSend={sendContactEmailMobile}
        />
      )}

      {/* Compose Email from Draft */}
      <ComposeEmailModal
        isOpen={!!composeFromDraft}
        onClose={() => setComposeFromDraft(null)}
        contact={contact || {}}
        template={composeFromDraft || undefined}
      />

      {/* Unsaved changes confirmation (mobile) */}
      <UnsavedChangesConfirmationModal
        show={showUnsavedConfirmation}
        onDismiss={() => setShowUnsavedConfirmation(false)}
        onClose={onClose}
      />
    </div>
  );

  // Render to dedicated modal portal root to escape any parent stacking contexts
  const portalRoot = document.getElementById('modal-portal-root');
  if (!portalRoot) {
    console.error('Modal portal root not found');
    return null;
  }
  return createPortal(modalContent, portalRoot);
}
